import { Injectable } from '@angular/core';
import { ApiDrawingCandidateStatus } from '@app/infrastructure/api/models';
import { DrawingCandidatesFilters } from '@app/shared/drawings/models';
import {
  SetDrawingCandidatesInMatchListConfig,
  SetDrawingCandidatesInReviewListConfig,
} from '@app/routes/index/pending-drawings/index/drawings/_actions';
import { DrawingCandidatesFilterValues } from '@app/routes/index/pending-drawings/index/drawings/_filters';
import { ROWS_PER_PAGE_OPTIONS } from '@app/shared/common/constants';
import { Action, Selector, State, StateContext } from '@ngxs/store';

export const CURRENT_DRAWING_CANDIDATES_LIST_VERSION = 3;

interface DrawingCandidatesListStateModel {
  version: number;
  itemsPerPage: number;
  filters?: DrawingCandidatesFilters;
  inReviewStatus?: ApiDrawingCandidateStatus;
  inReviewLowAiConfidenceOnly?: boolean;
  inMatchStatus?: ApiDrawingCandidateStatus;
}

export const DRAWING_CANDIDATES_LIST_STATE_DEFAULTS: DrawingCandidatesListStateModel = {
  version: CURRENT_DRAWING_CANDIDATES_LIST_VERSION,
  itemsPerPage: ROWS_PER_PAGE_OPTIONS[0],
};

@State<DrawingCandidatesListStateModel>({
  name: 'DrawingCandidatesList',
  defaults: DRAWING_CANDIDATES_LIST_STATE_DEFAULTS,
})
@Injectable()
export class DrawingCandidatesListState {
  @Selector()
  static itemsPerPage(state: DrawingCandidatesListStateModel): number {
    return state.itemsPerPage;
  }

  @Selector()
  static filters(state: DrawingCandidatesListStateModel): DrawingCandidatesFilters | undefined {
    return state.filters;
  }

  @Selector()
  static inReviewStatus(
    state: DrawingCandidatesListStateModel,
  ): ApiDrawingCandidateStatus | undefined {
    return state.inReviewStatus;
  }

  @Selector()
  static inReviewLowAiConfidenceOnly(state: DrawingCandidatesListStateModel): boolean | undefined {
    return state.inReviewLowAiConfidenceOnly;
  }

  @Selector()
  static inMatchStatus(
    state: DrawingCandidatesListStateModel,
  ): ApiDrawingCandidateStatus | undefined {
    return state.inMatchStatus;
  }

  @Action(SetDrawingCandidatesInReviewListConfig)
  setInReviewListConfig(
    ctx: StateContext<DrawingCandidatesListStateModel>,
    action: SetDrawingCandidatesInReviewListConfig,
  ) {
    ctx.patchState({
      itemsPerPage: action.config.pagination.itemsPerPage as number,
      filters: filtersOf(action.config),
      inReviewStatus: action.config.status ?? undefined,
      inReviewLowAiConfidenceOnly: action.config.isLowAiConfidenceOnly ?? undefined,
    });
  }

  @Action(SetDrawingCandidatesInMatchListConfig)
  setInMatchListConfig(
    ctx: StateContext<DrawingCandidatesListStateModel>,
    action: SetDrawingCandidatesInMatchListConfig,
  ) {
    ctx.patchState({
      itemsPerPage: action.config.pagination.itemsPerPage as number,
      filters: filtersOf(action.config),
      inMatchStatus: action.config.status ?? undefined,
    });
  }
}

function filtersOf(config: DrawingCandidatesFilterValues): DrawingCandidatesFilters {
  return {
    commodityIds: config.commodityIds ?? undefined,
    consigneeIds: config.consigneeIds ?? undefined,
    portIds: config.portIds ?? undefined,
    supplierIds: config.supplierIds ?? undefined,
    fileNames: config.fileNames ?? undefined,
    createdFrom: config.createdFrom ?? undefined,
    createdTo: config.createdTo ?? undefined,
  };
}
